import React, { useState, useEffect } from 'react';
import { Key, Copy, Trash2, Plus, ExternalLink, Code, BookOpen, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const API = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    expiresInDays: ''
  });

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api-tokens');
      setTokens(response.data.tokens || []);
    } catch (error) {
      if (error.response?.data?.error?.code === 'API_NOT_ENABLED') {
        toast.error('API access is not enabled for your account. Please contact support.');
      } else {
        toast.error('Failed to fetch API tokens');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api-tokens', formData);
      setNewToken(response.data.token);
      setFormData({ name: '', expiresInDays: '' });
      fetchTokens();
      toast.success('API token created successfully');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to create API token');
    }
  };

  const handleDeleteToken = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this API token? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/api-tokens/${id}`);
      fetchTokens();
      toast.success('API token revoked successfully');
    } catch (error) {
      toast.error('Failed to revoke API token');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Access</h1>
          <p className="text-gray-600">Manage API tokens and integrate Acquiva into your systems</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Token
        </button>
      </div>

      {/* Documentation Links */}
      <div className="grid md:grid-cols-3 gap-4">
        <a
          href="/docs/api"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-blue-50 rounded-lg p-4 hover:bg-blue-100 transition-colors flex items-start space-x-3"
        >
          <BookOpen className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-900">API Documentation</h3>
            <p className="text-sm text-gray-600">Complete API reference and guides</p>
          </div>
          <ExternalLink className="w-4 h-4 text-blue-600 ml-auto" />
        </a>

        <div className="bg-purple-50 rounded-lg p-4 flex items-start space-x-3">
          <Code className="w-5 h-5 text-purple-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-900">Quick Start</h3>
            <p className="text-sm text-gray-600">Get started in minutes</p>
          </div>
        </div>

        <a
          href="mailto:support@acquiva.com"
          className="bg-green-50 rounded-lg p-4 hover:bg-green-100 transition-colors flex items-start space-x-3"
        >
          <AlertCircle className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-900">Need Help?</h3>
            <p className="text-sm text-gray-600">Contact our support team</p>
          </div>
        </a>
      </div>

      {/* Example Code */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Example Usage</h2>
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-sm text-gray-100">
            <code>{`# Create a verification request
curl -X POST https://api.acquiva.co/api/v1/requests \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "KYC",
    "level": "STANDARD",
    "subjectName": "John Smith",
    "subjectEmail": "john@example.com"
  }'`}</code>
          </pre>
        </div>
      </div>

      {/* API Tokens List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Your API Tokens</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : tokens.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No API tokens yet. Create one to get started.
            </div>
          ) : (
            tokens.map((token) => (
              <div key={token.id} className="p-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Key className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{token.name}</h3>
                    <p className="text-sm text-gray-500">
                      {token.token_prefix}••••••••
                    </p>
                    <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                      <span>Created {formatDate(token.created_at)}</span>
                      {token.last_used_at && (
                        <span>Last used {formatDate(token.last_used_at)}</span>
                      )}
                      {token.expires_at && (
                        <span>Expires {formatDate(token.expires_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    token.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {token.status}
                  </span>
                  <button
                    onClick={() => handleDeleteToken(token.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Revoke token"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Token Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Create API Token</h2>
            <form onSubmit={handleCreateToken} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Token Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Production API, Development"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expires In (days)
                </label>
                <input
                  type="number"
                  value={formData.expiresInDays}
                  onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })}
                  placeholder="Leave empty for no expiration"
                  min="1"
                  max="365"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Token
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Token Display Modal */}
      {newToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">API Token Created</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800 font-medium">
                ⚠️ Save this token now - you won't be able to see it again!
              </p>
            </div>
            <div className="bg-gray-900 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <code className="text-sm text-gray-100 break-all">{newToken}</code>
                <button
                  onClick={() => copyToClipboard(newToken)}
                  className="ml-2 p-2 text-gray-400 hover:text-white transition-colors"
                  title="Copy to clipboard"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <button
              onClick={() => {
                setNewToken(null);
                setShowCreateModal(false);
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              I've saved my token
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default API;

